import cookies from 'js-cookie';
import {
  buildCashierBoxMovementReceiptEscPos,
  buildCashierBoxMovementReceiptViewModel,
  type CashierBoxMovementReceiptLabels,
} from '@washday/utilities';
import type { ICashierBox, IInOutMovement, IStore } from '../interfaces/Store';
import { openEscPosFallbackTab } from './escposFallbackTab';
import { showNotification, updateNotification } from './Notification/Notification';
import Printer from './qz-print';
import { createEscPosQzPrintData, resolveEscPosPaperWidth } from './ticketEscPos';

type ReceiptTranslator = (key: string) => string;

interface TryPrintCashierBoxMovementReceiptParams {
  movement: IInOutMovement;
  cashierBox?: ICashierBox | null;
  store?: IStore | null;
  t: ReceiptTranslator;
  failureTitle?: string;
}

function getErrorMessage(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined;
}

function buildReceiptLabels(t: ReceiptTranslator): CashierBoxMovementReceiptLabels {
  return {
    title: t('CashierboxesPage:movementReceipt.title'),
    typeLabel: t('CashierboxesPage:movementReceipt.type'),
    dateLabel: t('CashierboxesPage:movementReceipt.date'),
    cashierBoxLabel: t('CashierboxesPage:movementReceipt.cashierBox'),
    amountLabel: t('CashierboxesPage:movementReceipt.amount'),
    conceptLabel: t('CashierboxesPage:movementReceipt.concept'),
    deliveredByLabel: t('CashierboxesPage:movementReceipt.deliveredBy'),
    receivedByLabel: t('CashierboxesPage:movementReceipt.receivedBy'),
    nameLabel: t('CashierboxesPage:movementReceipt.name'),
    signatureLabel: t('CashierboxesPage:movementReceipt.signature'),
    noConceptLabel: t('CashierboxesPage:movementReceipt.noConcept'),
    movementTypes: {
      in: t('CashierboxesPage:CRUD.movementTypeOptions.in'),
      out: t('CashierboxesPage:CRUD.movementTypeOptions.out'),
    },
  };
}

function updateFallbackNotification({
  bytes,
  failureTitle,
  notificationId,
  reason,
  t,
}: {
  bytes: Uint8Array;
  failureTitle: string;
  notificationId: string;
  reason: string;
  t: ReceiptTranslator;
}): void {
  const opened = openEscPosFallbackTab({
    bytes,
    title: t('CashierboxesPage:movementReceipt.title'),
  });

  updateNotification({
    id: notificationId,
    loading: false,
    title: failureTitle,
    message: `${reason} ${
      opened
        ? t('CashierboxesPage:movementReceipt.fallbackOpened')
        : t('CashierboxesPage:movementReceipt.fallbackBlocked')
    }`,
  });
}

export async function tryPrintCashierBoxMovementReceipt({
  movement,
  cashierBox,
  store,
  t,
  failureTitle,
}: TryPrintCashierBoxMovementReceiptParams): Promise<boolean> {
  if (import.meta.env.VITE_E2E_DISABLE_QZ === '1') {
    return false;
  }

  const resolvedFailureTitle = failureTitle || t('CashierboxesPage:movementReceipt.printError');
  const notificationId = showNotification({
    loading: true,
    title: t('CashierboxesPage:movementReceipt.preparingPrint'),
    type: 'Info',
  });
  let receiptBytes: Uint8Array | null = null;

  try {
    const printerName = cookies.get('printer') || localStorage.getItem('printer');
    const printerPaperSize =
      cookies.get('printerPaperSize') || localStorage.getItem('printerPaperSize');
    const paperWidth = resolveEscPosPaperWidth(printerPaperSize);
    const viewModel = buildCashierBoxMovementReceiptViewModel({
      movement,
      cashierBox,
      store,
      labels: buildReceiptLabels(t),
    });
    receiptBytes = buildCashierBoxMovementReceiptEscPos(viewModel, {
      paperWidth,
      includeCut: true,
      feedLinesBeforeCut: 4,
    });

    if (!printerName) {
      updateFallbackNotification({
        bytes: receiptBytes,
        failureTitle: resolvedFailureTitle,
        notificationId,
        reason: t('CashierboxesPage:movementReceipt.noPrinter'),
        t,
      });
      return false;
    }

    const printer = new Printer();
    const connectResponse = await printer.verifyQZConnection(printerName);

    if (!connectResponse.ok) {
      updateFallbackNotification({
        bytes: receiptBytes,
        failureTitle: resolvedFailureTitle,
        notificationId,
        reason: connectResponse.message || t('CashierboxesPage:movementReceipt.qzError'),
        t,
      });
      return false;
    }

    await printer.print(
      printerName,
      {
        jobName: 'Washday - Cashier Box Movement Receipt',
      },
      createEscPosQzPrintData(receiptBytes),
    );

    updateNotification({
      id: notificationId,
      loading: false,
      title: t('CashierboxesPage:movementReceipt.printSuccess'),
      message: printerName,
    });
    return true;
  } catch (error) {
    if (receiptBytes) {
      updateFallbackNotification({
        bytes: receiptBytes,
        failureTitle: resolvedFailureTitle,
        notificationId,
        reason: getErrorMessage(error) || t('CashierboxesPage:movementReceipt.printError'),
        t,
      });
      return false;
    }

    updateNotification({
      id: notificationId,
      loading: false,
      title: resolvedFailureTitle,
      message: getErrorMessage(error),
    });
    return false;
  }
}
